import React, { useState, useEffect, useRef } from 'react';
import { Wallet, ChevronDown, ArrowUpRight, ArrowDownRight, RefreshCw, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface WalletTransaction {
  id: string;
  amount: number;
  type: 'deposit' | 'withdrawal' | 'reward' | 'refund';
  description: string;
  created_at: string;
}

interface WithdrawalRequest {
  id: string;
  amount: number;
  wallet_address: string;
  status: 'pending' | 'completed' | 'rejected';
  created_at: string;
  processed_at: string | null;
  notes: string | null;
  transaction_hash: string | null;
}

const WalletMenu: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [showFeeWarning, setShowFeeWarning] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchTransactions();
      fetchWithdrawalRequests();
    }
  }, [isOpen]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('wallet_transactions')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setTransactions(data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const fetchWithdrawalRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWithdrawalRequests(data || []);
    } catch (error) {
      console.error('Error fetching withdrawal requests:', error);
      toast.error('Failed to load withdrawal requests');
    }
  };

  const handleWithdrawalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const amount = parseFloat(withdrawalAmount);
      if (isNaN(amount) || amount <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }

      if (!walletAddress.trim()) {
        toast.error('Please enter a wallet address');
        return;
      }

      // Show fee warning for amounts under 10,000
      if (amount < 10000 && !showFeeWarning) {
        setShowFeeWarning(true);
        return;
      }

      const { error } = await supabase.rpc('request_withdrawal', {
        amount,
        wallet_address: walletAddress.trim()
      });

      if (error) throw error;

      toast.success('Withdrawal request submitted successfully');
      setWithdrawalAmount('');
      setWalletAddress('');
      setShowFeeWarning(false);
      setShowWithdrawalForm(false);
      fetchTransactions();
      fetchWithdrawalRequests();
    } catch (error) {
      console.error('Error submitting withdrawal request:', error);
      toast.error('Failed to submit withdrawal request');
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownRight className="text-green-500" />;
      case 'withdrawal':
        return <ArrowUpRight className="text-red-500" />;
      case 'reward':
        return <CheckCircle className="text-blue-500" />;
      case 'refund':
        return <RefreshCw className="text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Pending</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Completed</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Rejected</span>;
      default:
        return null;
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
      >
        <Wallet className="h-5 w-5 text-gray-500 dark:text-gray-400" />
        <span className="text-gray-700 dark:text-gray-200 font-medium">
          ₽{user?.balance.toLocaleString('ru-RU')}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Wallet</h3>
              <button
                onClick={() => setShowWithdrawalForm(true)}
                className="px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                Withdraw
              </button>
            </div>

            {showWithdrawalForm ? (
              <>
                {showFeeWarning ? (
                  <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900 rounded-lg">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2" />
                      <div>
                        <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                          Processing Fee Notice
                        </h4>
                        <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                          Withdrawals under ₽10,000 incur a ₽500 processing fee. Your total deduction will be ₽{(parseFloat(withdrawalAmount) + 500).toLocaleString('ru-RU')}. Do you wish to continue?
                        </p>
                        <div className="mt-3 flex space-x-2">
                          <button
                            onClick={() => handleWithdrawalSubmit(new Event('submit') as any)}
                            className="px-3 py-1.5 text-sm font-medium text-yellow-800 dark:text-yellow-200 bg-yellow-100 dark:bg-yellow-800 rounded-md hover:bg-yellow-200 dark:hover:bg-yellow-700"
                          >
                            Continue with Fee
                          </button>
                          <button
                            onClick={() => setShowFeeWarning(false)}
                            className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleWithdrawalSubmit} className="mb-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Amount (₽)
                      </label>
                      <input
                        type="number"
                        value={withdrawalAmount}
                        onChange={(e) => setWithdrawalAmount(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="Enter amount"
                        min="0.01"
                        step="any"
                      />
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Note: Withdrawals under ₽10,000 incur a ₽500 processing fee
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        USDT TRC-20 Wallet Address
                      </label>
                      <input
                        type="text"
                        value={walletAddress}
                        onChange={(e) => setWalletAddress(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="Enter wallet address"
                      />
                    </div>

                    <div className="flex space-x-2">
                      <button
                        type="submit"
                        className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                      >
                        Submit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowWithdrawalForm(false);
                          setShowFeeWarning(false);
                        }}
                        className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <>
                <div className="space-y-2 mb-4">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300">Recent Transactions</h4>
                  {loading ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                    </div>
                  ) : transactions.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                      No recent transactions
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {transactions.map((transaction) => (
                        <div
                          key={transaction.id}
                          className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            {getTransactionIcon(transaction.type)}
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {transaction.description}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(transaction.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <span className={`font-medium ${
                            transaction.amount > 0 
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString('ru-RU')} ₽
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300">Withdrawal Requests</h4>
                  {withdrawalRequests.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                      No withdrawal requests
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {withdrawalRequests.map((request) => (
                        <div
                          key={request.id}
                          className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-1"
                        >
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              ₽{request.amount.toLocaleString('ru-RU')}
                            </span>
                            {getStatusBadge(request.status)}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(request.created_at).toLocaleString()}
                          </p>
                          {request.transaction_hash && (
                            <a
                              href={`https://tronscan.org/#/transaction/${request.transaction_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                            >
                              View Transaction
                            </a>
                          )}
                          {request.notes && (
                            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                              {request.notes}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletMenu;